import { useState } from 'react'
import { CheckCircle2 } from 'lucide-react'

export interface ProviderQuery {
  id: string
  encounter_id: string
  category: string
  title: string
  body: string | null
  chart_anchor_text: string | null
  status: string
  patient_name: string | null
  patient_last_name: string | null
  patient_dob: string | null
  date_of_service: string | null
  claim_id: string | null
  facility_name: string | null
  surgeon_name: string | null
  account_no: string | null
  outcome_choice?: 'documentation_amended' | 'clarification_received' | 'no_change_needed'
}

interface QueryResponseFormProps {
  queries: ProviderQuery[]
  onSubmit: (queryId: string, outcome: ProviderQuery['outcome_choice'], freeText: string | null) => Promise<void>
}

export function QueryResponseForm({ queries, onSubmit }: QueryResponseFormProps) {
  const remaining = queries.filter((q) => q.status !== 'recipient_responded' && q.status !== 'resolved').length

  return (
    <div className="public-response-form">
      {remaining > 0 && remaining < queries.length && (
        <div className="public-response-card public-response-card--info public-response-card--compact">
          <strong>{remaining} quer{remaining === 1 ? 'y' : 'ies'} left to answer.</strong>
        </div>
      )}

      {queries.length === 0 && (
        <div className="public-response-card public-response-card--compact">
          <p style={{ margin: 0 }}>No queries are currently awaiting your response.</p>
        </div>
      )}

      {queries.map((q) => (
        <QueryCard key={q.id} query={q} onSubmit={onSubmit} />
      ))}
    </div>
  )
}

function QueryCard({ query, onSubmit }: { query: ProviderQuery; onSubmit: QueryResponseFormProps['onSubmit'] }) {
  const [outcome, setOutcome] = useState<ProviderQuery['outcome_choice']>(undefined)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(query.status === 'recipient_responded' || query.status === 'resolved')

  async function submit() {
    if (!outcome) return
    setBusy(true)
    try {
      await onSubmit(query.id, outcome, text.trim() || null)
      setDone(true)
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="public-response-card public-response-card--done public-response-card--compact">
        <CheckCircle2 size={14} /> Response received for <strong>{query.title}</strong>.
      </div>
    )
  }

  return (
    <div className="public-response-card public-response-card--compact">
      <div className="public-response-card__patient">
        {[
          query.claim_id ? `Claim ${query.claim_id}` : null,
          query.patient_name ?? query.patient_last_name,
          query.date_of_service ? `DOS ${query.date_of_service}` : null,
          query.facility_name,
        ].filter(Boolean).join(' · ')}
      </div>
      <h2 className="public-response-card__title">{query.title}</h2>
      {query.body && <p className="public-response-card__body">{query.body}</p>}

      {query.chart_anchor_text && (
        <blockquote className="public-response-card__anchor">{query.chart_anchor_text}</blockquote>
      )}

      <fieldset className="public-response-card__choices public-response-card__choices--compact">
        <legend>Choose one</legend>
        {([
          { value: 'documentation_amended',  label: 'Amend documentation' },
          { value: 'clarification_received', label: 'Clarify' },
          { value: 'no_change_needed',       label: 'No change needed' },
        ] as const).map((o) => (
          <label key={o.value} className={`public-response-choice public-response-choice--compact${outcome === o.value ? ' is-selected' : ''}`}>
            <input
              type="radio"
              name={`outcome-${query.id}`}
              value={o.value}
              checked={outcome === o.value}
              onChange={() => setOutcome(o.value)}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </fieldset>

      <textarea
        className="public-response-card__textarea public-response-card__textarea--compact"
        rows={2}
        placeholder="Additional comments (optional)"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />

      <button
        type="button"
        className="public-response-submit public-response-submit--compact"
        disabled={!outcome || busy}
        onClick={submit}
      >
        {busy ? 'Submitting…' : 'Submit response'}
      </button>
    </div>
  )
}
